import { HHPaper, HHStack } from '@hinge-health/react-component-library';
import { ActiveContractLabels } from '../../../constants/strings/billing-label-enums';
import {
  ContractPackagesType,
  ContractTemplateType,
  ContractType,
} from '../../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../utils/bills-utils';
import { formatCurrency } from '../../../utils/currency-helpers';
import { computeEffectiveRange } from '../../../utils/date-helper';
import { IDiscount } from '../../../utils/discount-utils';
import MetaInfoDisplay from '../../utils/meta-info-display';
import { TextGrouping } from '../../utils/text-grouping';
import { PackageDetailCard } from './package-detail-card';

interface ProductPackageContractViewProps {
  contract: ContractType;
  productPackages: { key: string; contractPackage: ContractPackagesType }[];
  templates: ContractTemplateType[];
  slimDisplay?: boolean;
  discounts?: IDiscount[];
}

const ProductPackageContractView = ({
  contract,
  productPackages,
  templates,
  slimDisplay = false,
  discounts,
}: ProductPackageContractViewProps): JSX.Element => (
  <HHStack sx={{ position: 'relative' }} gap={2}>
    {/* Contract Details */}
    <HHStack
      display="grid"
      gridTemplateColumns={slimDisplay ? '1fr' : 'repeat(5, 1fr)'}
      gap={slimDisplay ? 2 : 0}
      padding={2}
    >
      <TextGrouping
        label={ActiveContractLabels.EFFECT_DATE_LABEL}
        text={computeEffectiveRange(contract.startDate, contract.endDate)}
      />
      <TextGrouping
        label={ActiveContractLabels.USER_ANNUAL_CAP}
        text={
          contract.userAnnualCap
            ? formatCurrency(contract.userAnnualCap, contract.currency)
            : 'No user annual spending cap found'
        }
      />
      <TextGrouping
        label={ActiveContractLabels.CURRENCY}
        text={contract.currency ?? 'Currency not found'}
      />
      <TextGrouping
        label="Billing Type"
        text={capitalizeFirstLetterAndSpaceSnakeString(
          contract.billingType ?? 'None',
        )}
      />
      <TextGrouping
        label="Global Daily Engagement Cap"
        text={contract.globalDailyEngagementCap?.toString() ?? 'None'}
      />
      <MetaInfoDisplay
        updatedAt={contract.updatedAt}
        createdAt={contract.createdAt}
        lastModifiedBy={contract.lastModifiedBy ?? '--'}
        floating
      />
    </HHStack>
    {/* Packages */}
    {productPackages.map(productPackage => (
      <HHPaper
        key={productPackage.key}
        elevation={3}
        sx={{ backgroundColor: 'background.default', padding: 4 }}
      >
        <PackageDetailCard
          productPackage={productPackage}
          contract={contract}
          templates={templates}
          slimDisplay={slimDisplay}
          discounts={discounts}
        />
      </HHPaper>
    ))}
  </HHStack>
);

export default ProductPackageContractView;
